import React, { useState } from 'react';
import { Client } from '@types';
import { Nav } from '@/components/Nav';
import MainMenu from '@/components/Nav/MainMenu';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';

function getIsSuperAdmin() {
  try {
    const raw = localStorage.getItem('User');
    return raw
      ? (JSON.parse(raw) as { isSuperAdmin?: boolean }).isSuperAdmin === true
      : false;
  } catch {
    return false;
  }
}

interface BoundaryState {
  error: Error | null;
}

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  BoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      return (
        <ErrorAlert
          isSuperAdmin={getIsSuperAdmin()}
          error={error}
          message={error.message}
          context='PageErrorBoundary'
        />
      );
    }
    return children;
  }
}

// eslint-disable-next-line @typescript-eslint/no-use-before-define
export { DashboardWrapper };
export default function DashboardWrapper({
  children,
  companyName,
  companyData,
}: {
  children: React.ReactNode;
  companyName?: string;
  companyData?: Client[];
}) {
  const [menuOpen, setMenuOpen] = useState(true);
  const menuClick = () => setMenuOpen(!menuOpen);

  return (
    <div className='h-screen flex flex-col'>
      <Nav
        companyName={companyName}
        companyData={companyData}
        onMenuClick={menuClick}
        isMenuOpen={menuOpen}
      />
      <div className='w-full'>
        <div className='flex flex-row flex-wrap'>
          <aside
            className={`hidden md:flex min-h-[(calc(100vh-70px))] bg-[#454648] transition-all ${
              menuOpen ? `w-[240px]` : 'w-0'
            } overflow-hidden ease-in-out duration-300`}
          >
            <div className='w-[240px] overflow-hidden grid content-between'>
              <MainMenu />
            </div>
          </aside>
          {/* The content has no width of its own, it fills whatever space the
              sidebar leaves. That way it follows the sidebar's own 300
              millisecond width animation frame by frame, and it takes a window
              resize at once. A width of its own with a transition made the
              content wider than the space beside the sidebar for 300
              milliseconds on every resize, and a width of its own without a
              transition did the same when the sidebar closed. In both cases the
              wrapping row dropped the content under the sidebar until the
              animation ended. */}
          <main
            role='main'
            className='flex-1 min-w-0 pt-1 px-2 h-[calc(100vh-100px)] overflow-y-scroll'
          >
            <PageErrorBoundary>{children}</PageErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}
